import { AlertTriangle } from 'lucide-react';

// Adult vital sign thresholds (configurable later)
const VITAL_THRESHOLDS = {
  temperature: {
    low: { value: 36.0, label: 'Low' },
    high: { value: 37.5, label: 'High' },
    critical_low: { value: 35.0, label: 'Critical Low' },
    critical_high: { value: 38.5, label: 'Critical High' }
  },
  bp_systolic: {
    low: { value: 90, label: 'Low' },
    high: { value: 140, label: 'High' },
    critical_low: { value: 80, label: 'Critical Low' },
    critical_high: { value: 180, label: 'Critical High' }
  },
  bp_diastolic: {
    low: { value: 60, label: 'Low' },
    high: { value: 90, label: 'High' },
    critical_low: { value: 50, label: 'Critical Low' },
    critical_high: { value: 120, label: 'Critical High' }
  },
  pulse_rate: {
    low: { value: 60, label: 'Low' },
    high: { value: 100, label: 'High' },
    critical_low: { value: 50, label: 'Critical Low' },
    critical_high: { value: 120, label: 'Critical High' }
  },
  oxygen_saturation: {
    low: { value: 95, label: 'Low' },
    critical_low: { value: 90, label: 'Critical Low' }
    // No high threshold for SpO2
  },
  gmr_mgdl: {
    low: { value: 70, label: 'Low' },
    high: { value: 100, label: 'High' },
    critical_low: { value: 54, label: 'Critical Low' },
    critical_high: { value: 250, label: 'Critical High' }
  },
  gmr_mmol: {
    low: { value: 3.9, label: 'Low' },
    high: { value: 5.6, label: 'High' },
    critical_low: { value: 3.0, label: 'Critical Low' },
    critical_high: { value: 13.9, label: 'Critical High' }
  }
};

// Get vital status based on value and thresholds
const getVitalStatus = (value, thresholdKey) => {
  if (!value || isNaN(parseFloat(value))) return { status: 'normal', color: 'slate', message: null };
  
  const numValue = parseFloat(value);
  const thresholds = VITAL_THRESHOLDS[thresholdKey];
  
  if (!thresholds) return { status: 'normal', color: 'slate', message: null };
  
  // Check critical values first
  if (thresholds.critical_low && numValue <= thresholds.critical_low.value) {
    return { status: 'critical', color: 'red', message: thresholds.critical_low.label };
  }
  if (thresholds.critical_high && numValue >= thresholds.critical_high.value) {
    return { status: 'critical', color: 'red', message: thresholds.critical_high.label };
  }
  
  // Check warning values
  if (thresholds.low && numValue < thresholds.low.value) {
    return { status: 'warning', color: 'amber', message: thresholds.low.label };
  }
  if (thresholds.high && numValue > thresholds.high.value) {
    return { status: 'warning', color: 'amber', message: thresholds.high.label };
  }
  
  return { status: 'normal', color: 'slate', message: null };
};

// Get BP combined status
const getBPStatus = (systolic, diastolic) => {
  const sysStatus = getVitalStatus(systolic, 'bp_systolic');
  const diaStatus = getVitalStatus(diastolic, 'bp_diastolic');
  
  // Return the more severe status
  if (sysStatus.status === 'critical' || diaStatus.status === 'critical') {
    return { 
      status: 'critical', 
      color: 'red', 
      message: sysStatus.status === 'critical' ? sysStatus.message : diaStatus.message 
    };
  }
  if (sysStatus.status === 'warning' || diaStatus.status === 'warning') {
    return { 
      status: 'warning', 
      color: 'amber', 
      message: sysStatus.status === 'warning' ? sysStatus.message : diaStatus.message 
    };
  }
  return { status: 'normal', color: 'slate', message: null };
};

// Get GMR status based on unit
const getGMRStatus = (value, unit) => {
  const thresholdKey = unit === 'mmol/L' ? 'gmr_mmol' : 'gmr_mgdl';
  return getVitalStatus(value, thresholdKey);
};

const VitalCard = ({ label, value, unit, thresholdKey, isBP = false, systolic, diastolic, gmrUnit }) => {
  let status;
  let displayValue = value || '-';
  
  if (isBP) {
    status = getBPStatus(systolic, diastolic);
    displayValue = systolic && diastolic ? `${systolic}/${diastolic}` : '-';
  } else if (thresholdKey === 'gmr') {
    status = getGMRStatus(value, gmrUnit);
  } else {
    status = getVitalStatus(value, thresholdKey);
  }
  
  const bgColors = {
    slate: 'bg-slate-50 border-slate-200',
    amber: 'bg-amber-50 border-amber-300',
    red: 'bg-red-50 border-red-300'
  };
  
  const textColors = {
    slate: 'text-slate-900',
    amber: 'text-amber-700',
    red: 'text-red-700'
  };
  
  const labelColors = {
    slate: 'text-slate-600',
    amber: 'text-amber-600',
    red: 'text-red-600'
  };

  return (
    <div 
      className={`${bgColors[status.color]} border rounded-lg p-3 relative`}
      title={status.message ? `${status.message} - ${displayValue} ${unit || ''}` : ''}
    >
      <p className={`text-xs ${labelColors[status.color]} font-medium`}>{label}</p>
      <div className="flex items-center justify-center gap-1">
        <p className={`font-bold ${textColors[status.color]}`}>{displayValue}</p>
        {status.message && (
          <AlertTriangle className={`w-3 h-3 ${status.color === 'red' ? 'text-red-500' : 'text-amber-500'}`} />
        )}
      </div>
      {unit && <p className={`text-xs ${labelColors[status.color]}`}>{unit}</p>}
      {status.message && (
        <p className={`text-xs mt-1 ${status.color === 'red' ? 'text-red-600' : 'text-amber-600'} font-medium`}>
          {status.message}
        </p>
      )}
    </div>
  );
};

export { VitalCard, getVitalStatus, getBPStatus, getGMRStatus, VITAL_THRESHOLDS };
export default VitalCard;
